// Rummy 500 against the computer (cards/rummy.ts), on Gin Rummy's table. To draw, tap the stock, or tap any card in
// the spread discard pile to take it and every card above it (you must meld that card this turn). Then tap cards in
// your hand to choose them: Meld puts down a set or run; with one card chosen, tap a meld on the table to lay it off;
// Discard (or tap the discard pile) ends your turn. The computer plays at a readable pace and says what it did. No
// undo: it would let you take back a move after seeing the computer's reply. The game saves as you go.
import { cardName, faceHtml, isRed, rankOf, suitOf, type Card } from "./card"
import {
  chooseDiscard, chooseDraw, choosePlays, deal, discardCard, drawStock, fitsMeld, gameOver, layOff, LEVELS, meld, meldKind, nextHand, playerHint, takeDiscard,
  target, whyNotDiscard, whyNotLayOff, whyNotMeld, whyNotTake, winner, type Level, type Play, type Rules, type RummyState,
} from "./rummy"
import { randomSeed } from "../lib/rng"
import { readJSON, writeJSON } from "../lib/store"
import { gameEvent } from "../lib/game-events"
import { gameProgress } from "../lib/stats"

interface Saved { v: 1; state: RummyState; level: Level; bySuit: boolean; match: string }

export class RummyGame {
  // The page says which rules: data-variant="basic" is the Rummy page, anything else Rummy 500.
  private readonly rules: Rules = this.el.dataset.variant === "basic" ? "basic" : "500"
  private readonly save = this.rules === "basic" ? "dt:save:rummy:v1" : "dt:save:rummy-500:v1"
  private readonly ruleset = this.rules === "basic" ? "rummy:vs-computer" : "rummy-500:vs-computer"
  private state: RummyState
  private level: Level = "standard"
  private bySuit = true
  private picked: Card[] = []
  private hinted: { cards: Card[]; pile?: number; stock?: boolean } | null = null
  private busy = false
  private timers: number[] = []
  private match: string
  private sentFirst = false
  private lastCpu: Card[] = [] // cards the computer just put down, marked until your next move
  private readonly status = document.querySelector<HTMLElement>("[data-status]")
  private readonly toolbar = document.querySelector<HTMLElement>("[data-toolbar]")

  constructor(private readonly el: HTMLElement) {
    const saved = readJSON<Saved>(this.save)
    if (saved?.v === 1 && saved.state?.hands?.length === 2) {
      this.state = saved.state; this.level = LEVELS.includes(saved.level) ? saved.level : "standard"; this.bySuit = saved.bySuit ?? true; this.match = saved.match
    } else {
      this.state = deal(randomSeed(), 1, [0, 0], 1, this.rules)
      this.match = this.state.seed
    }
    this.toolbar?.addEventListener("click", (e) => {
      const act = (e.target as HTMLElement).closest<HTMLElement>("[data-act]")?.dataset.act
      if (act === "hint") this.showHint()
      else if (act === "sort") { this.bySuit = !this.bySuit; this.persist(); this.render(); this.say(this.bySuit ? "Sorted by suit." : "Sorted by rank.") }
      else if (act === "new") this.newGame()
    })
    el.addEventListener("click", (e) => this.onClick(e))
    el.addEventListener("change", (e) => { const t = e.target as HTMLSelectElement; if (t.name === "rummy-level") { this.level = t.value as Level; this.persist(); this.say(`Computer: ${t.selectedOptions[0]?.textContent}.`) } })
    el.addEventListener("keydown", (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey || (e.target as HTMLElement).closest("select")) return
      if (e.key === "h" || e.key === "H") this.showHint()
      if (e.key === "Escape") { this.picked = []; this.render(); this.say("Selection cleared.") }
    })
    this.render()
    if (saved) this.say("Welcome back. Your game is where you left it.")
    else this.say(this.state.turn === 0 ? "New game. Your turn: draw a card." : "New game. The computer goes first.")
    this.maybeComputer()
  }

  private get pace(): number { return document.documentElement.dataset.motion === "off" ? 250 : 850 }
  private persist(): void { writeJSON(this.save, { v: 1, state: this.state, level: this.level, bySuit: this.bySuit, match: this.match } satisfies Saved) }
  private say(msg: string): void { if (this.status && msg) this.status.textContent = msg }
  private later(fn: () => void, ms: number): void { const id = window.setTimeout(() => { this.timers = this.timers.filter((t) => t !== id); fn() }, ms); this.timers.push(id) }
  private myTurn(): boolean { return this.state.turn === 0 && this.state.phase !== "over" && !this.busy }
  private first(): void { if (!this.sentFirst) { this.sentFirst = true; gameEvent({ type: "first_move", ruleset: this.ruleset, daily: false }) } }

  private set(next: RummyState): void {
    const wasOver = this.state.phase === "over"
    this.state = next
    this.picked = this.picked.filter((c) => next.hands[0].includes(c))
    this.hinted = null
    this.persist()
    this.render()
    if (!wasOver && next.phase === "over") this.announce()
  }

  // ---- your moves --------------------------------------------------------------------------------------------------
  private onClick(e: Event): void {
    const t = e.target as HTMLElement
    const act = t.closest<HTMLElement>("[data-rm]")?.dataset.rm
    if (act === "next") { this.lastCpu = []; this.set(nextHand(this.state, randomSeed())); this.say(this.state.turn === 0 ? `Hand ${this.state.hand}. Your turn: draw.` : `Hand ${this.state.hand}. The computer goes first.`); return this.maybeComputer() }
    if (act === "new") return this.newGame()
    if (!act && !t.closest("[data-card]")) return
    if (!this.myTurn()) return this.say(this.state.phase === "over" ? "This hand is over." : "Wait for the computer to finish its turn.")
    const s = this.state
    if (act === "stock") {
      if (s.phase !== "draw") return this.say("You've drawn already. Meld or lay off if you can, then discard.")
      this.first(); this.lastCpu = []
      const n = drawStock(s)
      this.set(n)
      if (n.phase === "over") return
      return this.say(`You drew the ${cardName(n.hands[0][n.hands[0].length - 1]!)}.`)
    }
    if (act?.startsWith("pile-")) {
      const i = Number(act.slice(5))
      if (s.phase === "play") {
        if (this.picked.length === 1) return this.discard(this.picked[0]!)
        return this.say("To discard, choose one card from your hand, then tap the discard pile (or choose Discard).")
      }
      const why = whyNotTake(s, i)
      if (why) return this.say(why)
      this.first(); this.lastCpu = []
      const took = s.discard.slice(i)
      this.set(takeDiscard(s, i))
      return this.say(`You took ${took.length === 1 ? `the ${cardName(took[0]!)}` : `${took.length} cards, down to the ${cardName(took[0]!)}`}. You must meld the ${cardName(took[0]!)} this turn.`)
    }
    if (act?.startsWith("meld-")) {
      const at = Number(act.slice(5))
      if (this.picked.length !== 1) return this.say("To lay off, choose one card from your hand, then tap the meld it goes on.")
      const c = this.picked[0]!
      const why = whyNotLayOff(s, c, at)
      if (why) return this.say(why)
      this.picked = []
      this.set(layOff(s, c, at))
      if (this.state.phase === "over") return
      return this.say(`You laid off the ${cardName(c)}.${this.mustText()}`)
    }
    if (act === "meld") {
      const cards = this.picked.slice()
      const why = whyNotMeld(s, cards)
      if (why) return this.say(why)
      this.picked = []
      this.set(meld(s, cards))
      if (this.state.phase === "over") return
      return this.say(`You melded ${cards.length} cards.${this.mustText()}`)
    }
    if (act === "discard") return this.picked.length === 1 ? this.discard(this.picked[0]!) : this.say("Choose one card to discard.")
    const card = t.closest<HTMLElement>("[data-card]")
    if (card) {
      if (s.phase === "draw") return this.say("First draw: tap the stock, or a card in the discard pile.")
      const c = Number(card.dataset.card)
      this.picked = this.picked.includes(c) ? this.picked.filter((x) => x !== c) : [...this.picked, c]
      this.render()
      this.say(this.pickedText())
    }
  }

  private mustText(): string { return this.state.mustUse !== null ? ` You still have to meld the ${cardName(this.state.mustUse)}.` : " Discard a card to end your turn." }

  private pickedText(): string {
    const p = this.picked
    if (!p.length) return "Nothing chosen."
    if (p.length === 1) {
      const where = this.state.melds.findIndex((m) => fitsMeld(m, p[0]!))
      return `${cardName(p[0]!)} chosen.${where >= 0 ? " It goes on a meld on the table: tap that meld to lay it off, or discard it." : " Choose Discard to throw it, or more cards to meld."}`
    }
    return meldKind(p) ? `${p.length} cards chosen: they make a ${meldKind(p)}. Choose Meld.` : `${p.length} cards chosen. They don't make a meld yet.`
  }

  private discard(c: Card): void {
    const why = whyNotDiscard(this.state, c)
    if (why) return this.say(why)
    this.picked = []
    this.set(discardCard(this.state, c))
    if (this.state.phase === "over") return
    this.say(`You discarded the ${cardName(c)}.`)
    this.maybeComputer()
  }

  // ---- the computer ------------------------------------------------------------------------------------------------
  private maybeComputer(): void {
    const s = this.state
    if (s.phase === "over" || s.turn !== 1 || this.busy) return
    this.busy = true
    this.render()
    this.later(() => {
      let t = this.state
      const d = chooseDraw(t, this.level)
      const took = d === null ? [] : t.discard.slice(d)
      t = d === null ? drawStock(t) : takeDiscard(t, d)
      const drew = d === null ? "The computer drew from the stock" : `The computer took ${took.length === 1 ? `the ${cardName(took[0]!)}` : `${took.length} cards from the discard pile, down to the ${cardName(took[0]!)}`}`
      if (t.phase === "over") { this.busy = false; this.set(t); return }
      this.set(t) // still busy: your hand stays inactive while the computer plays
      this.say(`${drew}.`)
      this.later(() => {
        let u = this.state
        const plays: Play[] = choosePlays(u, this.level)
        const down: Card[] = []
        for (const p of plays) { u = p.kind === "meld" ? meld(u, p.cards) : layOff(u, p.card, p.at); down.push(...(p.kind === "meld" ? p.cards : [p.card])) }
        let thrown: Card | null = null
        if (u.phase === "play") { thrown = chooseDiscard(u, this.level); if (thrown !== null) u = discardCard(u, thrown) }
        this.busy = false
        this.lastCpu = down
        const melded = plays.reduce((n, p) => n + (p.kind === "meld" ? p.cards.length : 0), 0), offs = plays.filter((p) => p.kind === "layoff").length
        const what = [melded ? `melded ${melded} cards` : "", offs ? `laid off ${offs} ${offs === 1 ? "card" : "cards"}` : "", thrown !== null ? `discarded the ${cardName(thrown)}` : ""].filter(Boolean)
        this.set(u)
        if (u.phase === "over") return
        this.say(`${drew}${what.length ? `, ${what.join(", ")}` : ""}. Your turn: draw.`)
      }, this.pace)
    }, this.pace)
  }

  private announce(): void {
    const s = this.state
    const r = s.result!
    const how = r.out === 0 ? "You went out." : r.out === 1 ? "The computer went out." : "The stock ran out."
    const line = `You: ${r.melded[0]} melded, ${r.left[0]} left in hand, ${r.points[0] >= 0 ? "+" : ""}${r.points[0]}. Computer: ${r.melded[1]} melded, ${r.left[1]} left, ${r.points[1] >= 0 ? "+" : ""}${r.points[1]}.`
    if (gameOver(s)) {
      const won = winner(s) === 0
      gameEvent({ type: "end", ruleset: this.ruleset, outcome: won ? "win" : "loss", moves: s.hand, undos: 0, hints: 0, deal: this.match, daily: false })
      return this.say(`${how} ${line} ${won ? "You won the game" : "The computer won the game"}, ${s.scores[0]} to ${s.scores[1]}. Choose New game to play again.`)
    }
    this.say(`${how} ${line} Score: you ${s.scores[0]}, the computer ${s.scores[1]}. Choose Next hand.`)
  }

  showHint(): void {
    if (!this.myTurn()) return this.say(this.state.phase === "over" ? "This hand is over." : "Wait for your turn.")
    const h = playerHint(this.state)
    if (!h) return this.say("No hint right now.")
    if (h.draw !== undefined) {
      if (h.draw === "stock") { this.hinted = { cards: [], stock: true }; this.render(); return this.say("Hint: draw from the stock. Nothing in the discard pile is worth taking.") }
      this.hinted = { cards: [], pile: h.draw }; this.render()
      return this.say(`Hint: take the discard pile down to the ${cardName(this.state.discard[h.draw]!)}, and meld it.`)
    }
    if (h.plays?.length) {
      const p = h.plays[0]!
      this.hinted = { cards: p.kind === "meld" ? p.cards : [p.card] }
      this.render()
      return this.say(p.kind === "meld" ? `Hint: meld ${p.cards.map(cardName).join(", ")}.` : `Hint: lay off the ${cardName(p.card)}.`)
    }
    if (h.discard !== undefined) { this.hinted = { cards: [h.discard] }; this.render(); return this.say(`Hint: discard the ${cardName(h.discard)}.`) }
  }

  newGame(): void {
    for (const id of this.timers) window.clearTimeout(id)
    this.timers = []
    this.busy = false
    if ((this.sentFirst || this.state.hand > 1) && !gameOver(this.state)) gameEvent({ type: "end", ruleset: this.ruleset, outcome: "new_game", moves: this.state.hand, undos: 0, hints: 0 })
    this.sentFirst = false; this.picked = []; this.lastCpu = []
    const fresh = deal(randomSeed(), 1, [0, 0], 1, this.rules)
    this.match = fresh.seed
    this.set(fresh)
    this.say(`New game, first to ${target(this.rules)}. ${fresh.turn === 0 ? "Your turn: draw." : "The computer goes first."}`)
    this.maybeComputer()
  }

  // ---- drawing -----------------------------------------------------------------------------------------------------
  render(): void {
    const s = this.state
    const mine = this.myTurn()
    const order = (a: Card, b: Card): number => (this.bySuit ? [0, 1, 3, 2].indexOf(suitOf(a)) - [0, 1, 3, 2].indexOf(suitOf(b)) || rankOf(a) - rankOf(b) : rankOf(a) - rankOf(b) || suitOf(a) - suitOf(b))
    const show = (c: Card, extra = ""): string => `<div class="card face${isRed(c) ? " red" : ""}${extra}" role="img" aria-label="${cardName(c)}">${faceHtml(c)}</div>`
    // The table's melds: tap one to lay off the card you chose.
    const melds = s.melds.length
      ? s.melds.map((m, i) => {
          const can = mine && s.phase === "play" && this.picked.length === 1 && fitsMeld(m, this.picked[0]!)
          const new_ = m.cards.some((c) => this.lastCpu.includes(c))
          return `<button type="button" class="rm-meld${can ? " is-target" : ""}${new_ ? " is-last" : ""}" data-rm="meld-${i}" aria-label="Meld: ${m.cards.map(cardName).join(", ")}${can ? ". Lay off here" : ""}"><span class="gin-group">${m.cards.map((c) => show(c, m.by[m.cards.indexOf(c)] === 1 ? " is-theirs" : "")).join("")}</span></button>`
        }).join("")
      : `<p class="rm-empty">No melds on the table yet.</p>`
    // The discard pile, spread: in your draw, each card is a button that takes it and everything above it.
    const canTake = mine && s.phase === "draw"
    const pile = s.discard.map((c, i) => {
      const hint = this.hinted?.pile === i
      const label = `${cardName(c)}${canTake ? `: take ${i === s.discard.length - 1 ? "it" : `it and the ${s.discard.length - 1 - i} above it`}` : i === s.discard.length - 1 && mine ? ": discard here" : ""}`
      return `<button type="button" class="card face${isRed(c) ? " red" : ""}${hint ? " is-hint" : ""}" data-rm="pile-${i}" aria-label="${label}">${faceHtml(c)}</button>`
    }).join("")
    const hand = s.hands[0].slice().sort(order).map((c) => {
      const p = this.picked.includes(c)
      const h = this.hinted?.cards.includes(c)
      return `<button type="button" class="card face${isRed(c) ? " red" : ""}${p ? " is-selected" : ""}${h ? " is-hint" : ""}${s.mustUse === c ? " is-must" : ""}" data-card="${c}" aria-label="${cardName(c)}${s.mustUse === c ? ", must be melded" : ""}${p ? ", chosen" : ""}"${mine && s.phase === "play" ? ` aria-pressed="${p}"` : ""}>${faceHtml(c)}</button>`
    }).join("")
    let actions = ""
    if (mine && s.phase === "play") actions = `<button type="button" class="btn btn-primary" data-rm="meld"${meldKind(this.picked) ? "" : ` aria-disabled="true"`}>Meld</button><button type="button" class="btn" data-rm="discard"${this.picked.length === 1 && !whyNotDiscard(s, this.picked[0]!) ? "" : ` aria-disabled="true"`}>Discard</button>`
    if (s.phase === "over") actions = gameOver(s) ? `<button type="button" class="btn btn-primary" data-rm="new">New game</button>` : `<button type="button" class="btn btn-primary" data-rm="next">Next hand</button>`
    const n = s.hands[1].length
    this.el.innerHTML =
      `<div class="gin rummy">` +
      `<p class="gin-score"><span>You <b>${s.scores[0]}</b></span><span>Computer <b>${s.scores[1]}</b></span><span class="gin-rules">First to ${target(this.rules)} · hand ${s.hand}</span></p>` +
      `<div class="gin-row gin-cpu"><div class="gin-group gin-backs" role="img" aria-label="The computer's hand, ${n} cards">${s.hands[1].map(() => `<div class="card down"></div>`).join("")}</div></div>` +
      `<div class="rm-table tiny-cards" role="group" aria-label="Melds on the table">${melds}</div>` +
      `<div class="gin-middle rm-middle"><div class="slot"><button type="button" class="stock${this.hinted?.stock ? " is-hint" : ""}" data-rm="stock" aria-label="Stock, ${s.stock.length} cards${canTake ? ". Draw" : ""}"><span>${s.stock.length}</span></button></div>` +
      `<div class="rm-pile gin-group" role="group" aria-label="Discard pile, ${s.discard.length} cards, top card last">${pile || `<span class="rm-empty">Empty</span>`}</div></div>` +
      `<p class="gin-turn" aria-hidden="true">${s.phase === "over" ? "Hand over" : this.busy || s.turn === 1 ? "Computer's turn…" : s.phase === "draw" ? "Your turn: draw" : s.mustUse !== null ? `Meld the ${cardName(s.mustUse)}` : "Meld, lay off, then discard"}</p>` +
      `<div class="gin-row gin-you"><div class="gin-group" role="group" aria-label="Your hand, ${s.hands[0].length} cards">${hand}</div></div>` +
      `<div class="gin-actions">${actions}</div>` +
      `<div class="gin-settings"><label>Computer <select name="rummy-level">${LEVELS.map((l) => `<option value="${l}"${l === this.level ? " selected" : ""}>${l[0]!.toUpperCase()}${l.slice(1)}</option>`).join("")}</select></label></div>` +
      `</div>`
    if (s.phase !== "over") gameProgress({ key: this.save, ruleset: this.ruleset, id: this.match, moves: s.hand, started: this.sentFirst || s.hand > 1, won: false })
    else if (gameOver(s)) gameProgress({ key: this.save, ruleset: this.ruleset, id: this.match, moves: s.hand, started: true, won: winner(s) === 0 })
  }
}
